"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { X } from "lucide-react";
import Link from "next/link";
import { toast } from "sonner";
import Cookies from "js-cookie";
import { signIn, getSession } from "next-auth/react";
import { useAuthStore } from "@store/useAuthStore";
import {
  authFormCardClassName,
  authFormLabelClassName,
  authFormSubmitClassName,
  authFormSubtitleClassName,
  authFormTitleClassName,
} from "@components/auth-layout/auth-form-card";
import { fieldTestId, modulePageTestId } from "@/utils/test-id";
import { Input } from "@/components/ui/Input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/styled-button";
import { cn } from "@/utils/cn";
import z, { ZodSchema } from "zod";
import { useForm, useStore } from "@tanstack/react-form";
import {
  TanStackFormItem,
  TanStackFormProvider,
} from "@components/ui/TanstackFormItem";

const AUTH_LOGIN_FORM = modulePageTestId("auth", "login", "form");

const LoginSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .email("Please enter a valid email"),
  password: z
    .string()
    .trim()
    .min(1, "Password is required")
    .min(6, "Password must be at least 6 characters"),
  rememberMe: z.boolean().default(true),
});

const toastDismissAction = (className: string) => ({
  actionButtonStyle: {
    padding: "0px",
    backgroundColor: "transparent",
  },
  action: {
    label: <X className={cn("h-4 w-4", className)} />,
    onClick: () => toast.dismiss(),
  },
});

export default function Login() {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (values: {
    email: string;
    password: string;
    rememberMe: boolean;
  }) => {
    setIsLoading(true);
    try {
      const result = await signIn("credentials", {
        email: values.email,
        password: values.password,
        redirect: false,
      });

      if (!result?.ok) {
        throw new Error(result?.error || "Login failed");
      }

      const session = await getSession();
      if (!session?.accessToken || !session.user) {
        throw new Error("Failed to load session");
      }

      const branches = session.branches ?? [];
      const defaultBranch = branches[0];
      const cookieOptions = values.rememberMe ? { expires: 365 } : undefined;

      Cookies.set("access_token", session.accessToken, cookieOptions);
      Cookies.set("user_id", session.user.id, cookieOptions);
      localStorage.setItem("access_token", session.accessToken);
      localStorage.setItem("user_id", session.user.id);

      const encodedBranches = encodeURIComponent(JSON.stringify(branches));
      Cookies.set("user_branches", encodedBranches, cookieOptions);
      localStorage.setItem("user_branches", encodedBranches);

      if (defaultBranch) {
        Cookies.set("branch_id", defaultBranch.key, cookieOptions);
        Cookies.set("id", defaultBranch.id, cookieOptions);
        localStorage.setItem("branch_id", defaultBranch.key);
        localStorage.setItem("id", defaultBranch.id);
      }

      useAuthStore.getState().setAuth({
        user: session.user,
        token: session.accessToken,
      });

      router.push(`/${defaultBranch?.key || "dashboard"}`);
      toast.success("Login Successful! Welcome back", toastDismissAction("text-green-700"));
    } catch (error) {
      toast.error("Invalid credentials, please try again", toastDismissAction("text-destructive"));
    } finally {
      setIsLoading(false);
    }
  };

  const form = useForm({
    defaultValues: {
      email: "",
      password: "",
      rememberMe: true,
    },
    validators: {
      onSubmit: LoginSchema as ZodSchema,
      onChange: LoginSchema as ZodSchema,
      onMount: LoginSchema as ZodSchema,
    },
    onSubmit: async ({ value }) => {
      await handleSubmit(value);
    },
  });
  const formValues = useStore(form.store, (state) => state.values);
  return (
    <TanStackFormProvider form={form}>
      <form
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
        className={authFormCardClassName}
      >
          <div>
            <p className={`${authFormTitleClassName} mb-1`}>Welcome</p>
            <p className={authFormSubtitleClassName}>
              Log in to your account to continue
            </p>
          </div>

          <div className="text-left">
          <TanStackFormItem
            name="email"
            label="Email"
            required
            labelProps={{
              className: authFormLabelClassName + " lg:col-span-12",
            }}
            inputProps={{
              className: "lg:col-span-12",
            }}
          >
            {(field) => (
              <Input
                type="email"
                heightClassName="h-11"
                placeholder="Enter your email"
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                value={field.state.value}
                error={Boolean(
                  field.state.meta.isTouched &&
                    field.state.meta.errors.length > 0 &&
                    !field.state.meta.isValid,
                )}
                testId={fieldTestId(AUTH_LOGIN_FORM, "email")}
              />
            )}
          </TanStackFormItem>
          </div>

          <div className="text-left">
          <TanStackFormItem
            name="password"
            label="Password"
            required
            labelProps={{
              className: authFormLabelClassName + " lg:col-span-12",
            }}
            inputProps={{
              className: "lg:col-span-12",
            }}
          >
            {(field) => (
              <Input
                type="password"
                heightClassName="h-11"
                placeholder="Enter your password"
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                value={field.state.value}
                error={Boolean(
                  field.state.meta.isTouched &&
                    field.state.meta.errors.length > 0 &&
                    !field.state.meta.isValid,
                )}
                testId={fieldTestId(AUTH_LOGIN_FORM, "password")}
              />
            )}
          </TanStackFormItem>
          </div>

          <div className="flex items-center justify-between">
            <label className="flex cursor-pointer items-center gap-2">
              <Checkbox
              checked={formValues.rememberMe}
                onCheckedChange={(checked) =>
                form.setFieldValue("rememberMe", checked === true)
                }
                testId={fieldTestId(AUTH_LOGIN_FORM, "remember-me")}
              />
              <span className="font-medium text-foreground">Remember Me</span>
            </label>
            <Link
              href="/forgot-password"
              className="text-sm font-medium text-foreground hover:text-primary"
            >
              Forgot password?
            </Link>
          </div>

        <form.Subscribe>
          {({ isValid, canSubmit, isSubmitting }) => (
            <div>
              <Button
                type="submit"
                variant="filled"
                typeColor="primary"
                className={cn(authFormSubmitClassName, "uppercase")}
                buttonLabel="Login"
                loading={isLoading || isSubmitting}
                disabled={!isValid || !canSubmit || isSubmitting}
                testId={modulePageTestId("auth", "login", "submit")}
              />
            </div>
      )}
        </form.Subscribe>
      </form>
    </TanStackFormProvider>
  );
}
